import React from 'react';
import { createPortal } from 'react-dom';
import {
  Users,
  CalendarDays,
  DollarSign,
  Award,
  Shield,
  Trophy,
  ArrowRight,
  CheckCircle2,
  Lock,
  Plus,
  FileText,
  MessageCircle,
  Search
} from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db, getCollectionPath } from '../../lib/firebase';
import { Tournament, Team, Config, Player, UserProfile, RankingConfig } from '../../types';
import { formatPairName } from '../../lib/utils';
import { RuleButton } from '../common/RuleButton';
import { PairAvatar } from '../common/PairAvatar';
import { WhatsAppVerificationModal } from '../common/WhatsAppVerificationModal';

interface PublicTournamentLobbyProps {
  tournament: Tournament;
  allTeams: Team[];
  players: Player[];
  allUsers?: UserProfile[];
  onPlayerClick: (id: string | null) => void;
  onJoin?: (id: string, partnerId: string) => void;
  onCancelJoin?: (id: string) => void;
  onOpenChat?: (organizerId: string) => void;
  config: Config;
  rankingConfig: RankingConfig;
}

export function PublicTournamentLobby({
  tournament,
  allTeams,
  players,
  allUsers = [],
  onPlayerClick,
  onJoin,
  onCancelJoin,
  onOpenChat,
  config,
  rankingConfig
}: PublicTournamentLobbyProps) {
  const auth = getAuth();
  const currentUser = auth.currentUser;

  const isPending = currentUser && tournament.pendingTeamIds?.some(teamId => {
    const team = allTeams.find(t => t.id === teamId);
    return team && (team.player1Id === currentUser.uid || team.player2Id === currentUser.uid);
  });

  const isRegistered = currentUser && tournament.registeredTeamIds?.some(teamId => {
    const team = allTeams.find(t => t.id === teamId);
    return team && (team.player1Id === currentUser.uid || team.player2Id === currentUser.uid);
  });

  const registeredTeams = allTeams.filter(team => tournament.registeredTeamIds?.includes(team.id));
  const isFull = (tournament.registeredTeamIds?.length || 0) >= (tournament.maxTeams || 16);
  const registrationOpen = tournament.lobbyStatus !== 'closed';

  const [showConfirm, setShowConfirm] = React.useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = React.useState('');
  const [partnerSearch, setPartnerSearch] = React.useState('');
  const [showPhonePrompt, setShowPhonePrompt] = React.useState(false);
  const [phoneInput, setPhoneInput] = React.useState('');
  const [isSavingPhone, setIsSavingPhone] = React.useState(false);
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // No-op useEffect removed

  // Build a unified list of potential partners: all players except self
  const potentialPartners = React.useMemo(() => {
    const merged: { id: string; name: string; category?: string; gender?: string; photoUrl?: string }[] = [];
    const addedIds = new Set<string>();

    players.forEach(p => {
      if (currentUser && p.id !== currentUser.uid) {
        merged.push({ id: p.id, name: p.name, category: p.category, gender: p.gender, photoUrl: p.photoUrl });
        addedIds.add(p.id);
      }
    });

    return merged.sort((a, b) => a.name.localeCompare(b.name));
  }, [players, currentUser]);

  const filteredPartners = partnerSearch.trim()
    ? potentialPartners.filter(p => p.name.toLowerCase().includes(partnerSearch.toLowerCase()))
    : potentialPartners;

  const getCategoryRank = (cat?: string) => {
    if (!cat) return 20;
    const c = cat.toLowerCase();
    if (c.includes('1ra') || c.includes('1era')) return 1;
    if (c.includes('2da')) return 2;
    if (c.includes('3ra')) return 3;
    if (c.includes('4ta')) return 4;
    if (c.includes('5ta')) return 5;
    if (c.includes('6ta')) return 6;
    if (c.includes('7ma')) return 7;
    if (c.includes('8va')) return 8;
    if (c.includes('suma')) {
      const match = c.match(/\d+/);
      return match ? parseInt(match[0], 10) : 15;
    }
    if (c.includes('veterano')) return 10;
    return 20;
  };

  const currentPlayer = players.find(p => p.id === currentUser?.uid);
  const selectedPartner = players.find(p => p.id === selectedPartnerId);

  const validateRegistration = () => {
    if (!currentUser || !selectedPartnerId) return { valid: false, errors: [] };
    const errors: string[] = [];

    const p1 = currentPlayer;
    const p2 = selectedPartner;

    if (!p1 || !p2) return { valid: false, errors: ['No se encontró información de los jugadores'] };

    // 0. Check if already registered
    const isP2AlreadyRegistered = allTeams.some(t => 
      (tournament.registeredTeamIds?.includes(t.id) || tournament.pendingTeamIds?.includes(t.id)) && 
      (t.player1Id === p2.id || t.player2Id === p2.id)
    );
    
    if (isP2AlreadyRegistered) {
      errors.push(`El jugador ${p2.name} ya está anotado en este torneo`);
    }

    // 1. Gender check
    const tournamentGender = tournament.gender?.toLowerCase() || 'sin_restriccion';
    const p1Gender = p1.gender?.toLowerCase() || 'masculino';
    const p2Gender = p2.gender?.toLowerCase() || 'masculino';

    if (tournamentGender === 'masculino') {
      if (p1Gender !== 'masculino' || p2Gender !== 'masculino') {
        errors.push('Este torneo es solo para Caballeros');
      }
    } else if (tournamentGender === 'femenino') {
      if (p1Gender !== 'femenino' || p2Gender !== 'femenino') {
        errors.push('Este torneo es solo para Damas');
      }
    } else if (tournamentGender === 'mixto') {
      const hasMale = p1Gender === 'masculino' || p2Gender === 'masculino';
      const hasFemale = p1Gender === 'femenino' || p2Gender === 'femenino';
      if (!(hasMale && hasFemale) || p1Gender === p2Gender) {
        errors.push('La pareja mixta debe estar formada por un Masculino y un Femenino');
      }
    }

    // 2. Category check
    const isFreeCategory = tournament.category?.toLowerCase().includes('libre') ||
      tournament.category?.toLowerCase().includes('restriccion') ||
      tournament.category?.toLowerCase().includes('restricción');

    const isSumaCategory = tournament.category?.toLowerCase().startsWith('suma');

    if (!isFreeCategory) {
      const tourneyRank = getCategoryRank(tournament.category);
      
      const p1Rank = p1.category ? getCategoryRank(p1.category) : tourneyRank;
      const p2Rank = p2.category ? getCategoryRank(p2.category) : tourneyRank;

      if (isSumaCategory) {
        const sumLimitMatch = tournament.category?.match(/\d+/);
        const sumLimit = sumLimitMatch ? parseInt(sumLimitMatch[0], 10) : 13;
        
        const effectiveP1Rank = p1.category ? p1Rank : Math.max(1, sumLimit - p2Rank);
        const effectiveP2Rank = p2.category ? p2Rank : Math.max(1, sumLimit - effectiveP1Rank);
        const pairSum = effectiveP1Rank + effectiveP2Rank;
        
        if (pairSum < sumLimit && p1.category && p2.category) {
          errors.push(`La suma de sus categorías (${p1Rank} + ${p2Rank} = ${pairSum}) no alcanza el mínimo de ${sumLimit} permitido.`);
        }
      } else {
        // Higher category (lower number) cannot join lower tournament
        if (p1Rank < tourneyRank && p1.category) {
          errors.push(`Tu categoría (${p1.category || 'N/C'}) es superior a la del torneo (${tournament.category})`);
        }
        if (p2Rank < tourneyRank && p2.category) {
          errors.push(`La categoría de tu pareja (${p2.category || 'N/C'}) es superior a la del torneo (${tournament.category})`);
        }
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  };

  const { valid: isDataValid, errors: validationErrors } = validateRegistration();

  const handleJoinClick = () => {
    if (!currentUser) {
      onJoin?.(tournament.id, ''); // Trigger login
      return;
    }
    if (!currentPlayer?.phone) {
      setShowPhonePrompt(true);
      return;
    }
    setShowConfirm(true);
  };

  const handleSavePhone = () => {
    // Cuando el modal de WhatsApp Verification termina, actualiza Firebase automáticamente.
    // Solo cerramos y abrimos el paso de confirmar pareja.
    setShowPhonePrompt(false);
    setShowConfirm(true);
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      {/* Tournament Info & Details Grid */}
      <div className="max-w-3xl mx-auto space-y-4 mb-6">
        {/* Row 1: Description Box */}
        {tournament.description && (
          <div className="flex flex-col justify-center items-center min-h-[60px] pb-2">
            <div className="space-y-2 text-center px-1">
              <h4 className="text-[9px] font-black italic uppercase opacity-40 tracking-widest">Acerca del Torneo</h4>
              <p className="text-xs md:text-sm opacity-80 leading-relaxed whitespace-pre-wrap font-medium">
                {tournament.description}
              </p>
            </div>
          </div>
        )}

        {/* Row 2: Info Grid (Format & Fee) */}
        <div className="grid grid-cols-2 gap-3 max-w-[280px] mx-auto w-full">
          {/* Format Box */}
          <div className="bg-white/[0.03] border border-white/5 py-2 px-2.5 rounded-2xl flex items-center justify-center gap-2.5 transition-all hover:bg-white/[0.05] h-full">
            <div className="w-6 h-6 shrink-0 rounded-full bg-theme-primary/10 flex items-center justify-center border border-theme-primary/20">
              <Award size={12} className="theme-primary-text" />
            </div>
            <div className="space-y-0.5 text-left flex-1">
              <h4 className="text-[7px] font-black uppercase opacity-40 tracking-widest leading-none mb-0.5">Formato</h4>
              <p className="font-black text-[9px] uppercase text-white leading-tight">
                {tournament.type === 'liga' ? 'Liga + Llaves' : 'Eliminación Directa'}
              </p>
            </div>
          </div>

          {/* Registration Fee */}
          <div className="bg-white/[0.03] border border-white/5 py-2 px-2.5 rounded-2xl flex items-center justify-center gap-2.5 transition-all hover:bg-white/[0.05] h-full">
            <div className="w-6 h-6 shrink-0 rounded-full bg-theme-primary/10 flex items-center justify-center border border-theme-primary/20">
              <DollarSign size={12} className="theme-primary-text" />
            </div>
            <div className="space-y-0 text-left flex-1">
              <h4 className="text-[7px] font-black uppercase tracking-widest opacity-40 leading-none mb-0.5">Inscripción</h4>
              {tournament.entryFee ? (
                <>
                  <p className="text-sm font-black theme-primary-text leading-none">
                    ${tournament.entryFee.toLocaleString()}
                  </p>
                  <p className="text-[7px] font-bold uppercase opacity-40 tracking-widest pt-0.5">por jugador</p>
                </>
              ) : (
                <p className="text-[10px] font-black uppercase theme-primary-text leading-tight tracking-wide">
                  A confirmar
                </p>
              )}
            </div>
          </div>
        </div>

          {/* Prizes & Points Combined Section */}
          <div className="flex flex-col justify-center gap-8 w-full">
            {/* Prizes Section */}
            <div className="w-full">
              {tournament.rewards && tournament.rewards.length > 0 ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <div className="h-[1px] w-8 bg-gradient-to-r from-transparent to-theme-primary/40" />
                    <Trophy size={16} className="theme-primary-text" />
                    <h4 className="text-[10px] font-black uppercase tracking-[0.15em] theme-primary-text leading-none">Premios (por pareja)</h4>
                    <div className="h-[1px] w-8 bg-gradient-to-l from-transparent to-theme-primary/40" />
                  </div>
                  <div className="flex flex-wrap justify-center gap-3">
                    {tournament.rewards.map((reward, i) => {
                      const rankLower = reward.rank.toLowerCase();
                      const is1st = rankLower.includes('1er') || rankLower.includes('1o') || rankLower.includes('campeon') || rankLower.includes('campeón');
                      const is2nd = rankLower.includes('2do') || rankLower.includes('2o') || rankLower.includes('subcampeon') || rankLower.includes('subcampeón');
                      const is3rd = rankLower.includes('3er') || rankLower.includes('3o') || rankLower.includes('tercer');

                      let rankColor = 'text-white/70';
                      let rankBg = 'bg-slate-600';
                      let rankHeaderBg = 'bg-black/40';
                      let rankBorder = 'border-slate-500/40';
                      let prizeTextColor = 'text-white';

                      if (is1st) {
                        rankBg = 'bg-yellow-400';
                        rankBorder = 'border-yellow-400/60';
                        rankColor = 'text-yellow-300';
                        prizeTextColor = 'text-black';
                      } else if (is2nd) {
                        rankBg = 'bg-slate-300';
                        rankBorder = 'border-slate-300/60';
                        rankColor = 'text-slate-300';
                        prizeTextColor = 'text-black';
                      } else if (is3rd) {
                        rankBg = 'bg-slate-800';
                        rankBorder = 'border-white/30';
                        rankColor = 'text-slate-400';
                        prizeTextColor = 'text-white';
                      }

                      return (
                        <div key={i} className={`relative overflow-hidden bg-black/60 border ${rankBorder} rounded-xl flex flex-col w-[76px] h-[74px]`}>
                          {/* Metallic shimmer sweep */}
                          <div className="absolute inset-0 -translate-x-full animate-[shimmer_3s_infinite] bg-gradient-to-r from-transparent via-white/30 to-transparent z-10 pointer-events-none" />
                          {/* Rank Header */}
                          <div className={`${rankHeaderBg} py-1.5 border-b ${rankBorder} text-center flex items-center justify-center h-[30px] rounded-t-xl`}>
                            <span className={`text-[8px] font-black uppercase ${rankColor} tracking-[0.1em] leading-none`}>
                              {reward.rank}
                            </span>
                          </div>

                          {/* Rank Body */}
                          <div className={`flex-1 flex flex-col items-center justify-center p-2 ${rankBg} rounded-b-xl`}>
                            <span className={`text-xs font-black uppercase tracking-tight ${prizeTextColor} leading-tight text-center break-words max-w-full`}>
                              {reward.prize}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-center opacity-30 italic py-4 text-[10px] font-bold uppercase tracking-widest">
                  Premios a confirmar
                </div>
              )}
            </div>

            {/* Ranking Points Preview Section */}
            <div className="flex flex-col items-center justify-center w-full">
              <div className="flex items-center justify-center gap-2 mb-4">
                <div className="h-[1px] w-4 bg-gradient-to-r from-transparent to-theme-primary/20" />
                <Award size={12} className="theme-primary-text opacity-60" />
                <h4 className="text-[9px] font-black uppercase tracking-[0.15em] theme-primary-text leading-none opacity-60">Ranking RPT (por jugador)</h4>
                <div className="h-[1px] w-4 bg-gradient-to-l from-transparent to-theme-primary/20" />
              </div>

              <div className="flex flex-wrap justify-center gap-2">
                {[
                  { label: 'Campeón', points: rankingConfig.champion },
                  { label: 'Subcampeón', points: rankingConfig.runnerUp },
                  rankingConfig.awardPointsTo === 'third_place' 
                    ? { label: 'Tercer Puesto', points: rankingConfig.thirdPlace || 0 }
                    : { label: 'Semis', points: rankingConfig.semifinalist }
                ].map((item, idx) => (
                  <div key={idx} className="bg-black/40 border border-white/5 rounded-lg py-1.5 px-3 flex flex-col items-center justify-center transition-all hover:bg-white/5 min-w-[75px]">
                    <span className="text-[7px] font-black uppercase opacity-40 tracking-tighter mb-0.5">{item.label}</span>
                    <div className="flex items-baseline gap-0.5">
                      <span className="text-xs font-black theme-primary-text">{item.points}</span>
                      <span className="text-[6px] font-bold opacity-30 uppercase">pts</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
      </div>

      {/* Registration Section - Centered and Grouped */}
      <div className="max-w-6xl mx-auto !mt-12 space-y-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start bg-white/[0.02] border border-white/5 p-6 md:p-10 rounded-[2.5rem] md:rounded-[3.5rem] shadow-2xl backdrop-blur-sm overflow-hidden">
          <div className="space-y-6 text-center py-4 flex flex-col items-center justify-center h-full">
            <div className="flex justify-center w-full">
              {isPending || isRegistered ? null : isFull ? (
                <div className="inline-flex items-center justify-center relative gap-2 pl-8 pr-4 py-1 rounded-full border text-[8px] font-black uppercase tracking-widest transition-all duration-500 bg-yellow-500/10 border-yellow-500/20 text-yellow-500 shadow-[0_0_15px_rgba(234,179,8,0.1)]">
                  <span className="absolute left-3 flex h-1.5 w-1.5 shrink-0">
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-yellow-500"></span>
                  </span>
                  <span>Cupo Completo</span>
                </div>
              ) : (
                <div className="inline-flex items-center justify-center relative gap-2 pl-8 pr-4 py-1 rounded-full border text-[8px] font-black uppercase tracking-widest transition-all duration-500 bg-lime-400/10 border-lime-400/30 text-lime-400 shadow-[0_0_15px_rgba(163,230,53,0.1)]">
                  <span className="absolute left-3 flex h-1.5 w-1.5 shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-lime-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-lime-400 animate-pulse shadow-[0_0_8px_rgba(163,230,53,0.4)]"></span>
                  </span>
                  <span className="animate-pulse">Inscripciones Abiertas</span>
                </div>
              )}
            </div>
            {!isRegistered && (
              <div className="space-y-2">
                <h4 className="text-2xl md:text-3xl font-black uppercase italic leading-tight theme-primary-text">
                  {isPending ? '¡Solicitud enviada!' : '¡Quiero sumarme!'}
                </h4>
                <p className={`opacity-50 font-medium uppercase tracking-[0.1em] whitespace-nowrap ${isPending ? 'text-[9px] md:text-[11px]' : 'text-[8px] md:text-[10px]'}`}>
                  {isPending
                    ? 'Están evaluando tu solicitud de inscripción'
                    : isFull
                      ? 'Cupo completo para este evento.'
                      : 'Asegura la participación de tu pareja hoy mismo.'}
                </p>
              </div>
            )}

            {isPending ? (
              <div className="flex flex-col gap-3 w-full lg:w-auto">
                <div className="bg-white/10 border border-white/50 px-6 py-3 rounded-xl flex items-center gap-2 justify-center lg:justify-start">
                  <CheckCircle2 size={16} className="text-amber-500" />
                  <span className="text-[10px] font-black uppercase text-amber-500 tracking-widest">Solicitud en proceso</span>
                </div>
                <button
                  onClick={() => onCancelJoin?.(tournament.id)}
                  className="px-6 py-2 rounded-xl border border-red-500/20 text-red-500 hover:bg-red-500/10 transition-all font-black uppercase text-[9px] tracking-widest"
                >
                  Cancelar Solicitud
                </button>
              </div>
            ) : isRegistered ? (
              <div className="bg-white/5 border border-lime-400/50 px-6 py-3 rounded-xl flex items-center gap-2 justify-center w-full max-w-[400px] mx-auto">
                <CheckCircle2 size={16} className="text-lime-400" />
                <span className="text-[10px] font-black uppercase text-lime-400 tracking-widest">¡Ya están anotados!</span>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 w-full">
                <button
                  onClick={handleJoinClick}
                  disabled={isFull || !registrationOpen}
                  className={`w-full sm:w-auto min-w-[240px] max-w-[400px] px-8 py-3 rounded-xl font-black uppercase text-[10px] tracking-widest shadow-xl transition-all ${isFull || !registrationOpen
                    ? 'bg-white/10 text-white/20 cursor-not-allowed'
                    : 'theme-primary-bg text-black hover:scale-105 active:scale-95 shadow-[0_10px_30px_rgba(var(--glow-rgb), 0.3)]'
                    }`}
                >
                  Unirse al Torneo <ArrowRight size={12} className="inline ml-1" />
                </button>
                <button
                  onClick={() => onOpenChat?.(tournament.organizerId)}
                  className="w-full sm:w-auto min-w-[240px] max-w-[400px] px-6 py-3 rounded-xl border border-white/10 bg-white/5 font-black uppercase text-[10px] tracking-widest shadow-xl hover:bg-white/10 hover:border-white/20 active:scale-95 transition-all flex items-center justify-center gap-2"
                >
                  <MessageCircle size={14} /> Contactar al Organizador
                </button>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="flex justify-center items-center px-1">
              <h5 className="text-[10px] font-black uppercase italic tracking-wider flex items-center gap-2 opacity-60">
                <Users size={14} className="theme-primary-text" />
                PAREJAS INSCRIPTAS <span className="text-[11px] font-black ml-1.5 theme-primary-text">{registeredTeams.length}</span>
              </h5>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
              {registeredTeams.map((team) => {
                const p1 = players.find(p => p.id === team.player1Id);
                const p2 = players.find(p => p.id === team.player2Id);

                return (
                  <div
                    key={team.id}
                    className={`border px-2 py-1.5 rounded-lg flex items-center transition-all group h-[42px] shadow-sm hover:theme-primary-border ${tournament.approvedTeamIds?.includes(team.id)
                      ? 'border-lime-400/40 bg-lime-400/[0.08] shadow-[0_0_15px_rgba(163,230,53,0.1)]'
                      : 'border-white/10 bg-white/[0.05]'
                      }`}
                  >
                    <div className="flex items-center gap-2 w-full">
                      <PairAvatar
                        p1Id={team.player1Id}
                        p2Id={team.player2Id}
                        p1Image={p1?.photoUrl}
                        p2Image={p2?.photoUrl}
                        className="w-7 h-7 shrink-0"
                        onPlayerClick={onPlayerClick}
                      />
                      <div className="truncate flex-1 flex items-center justify-between gap-2">
                        <p className="font-black uppercase text-[8px] md:text-[9px] truncate tracking-tighter leading-tight text-white/90">
                          {formatPairName(team.name, 'short')}
                        </p>
                        {tournament.approvedTeamIds?.includes(team.id) && (
                          <CheckCircle2 size={11} className="text-lime-400 shrink-0 animate-in fade-in zoom-in duration-300" />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {registeredTeams.length === 0 && (
                <div className="col-span-full py-16 text-center opacity-30 border-2 border-dashed border-white/5 rounded-[2rem] bg-black/20">
                  <Users size={32} className="mx-auto mb-3" />
                  <p className="font-black uppercase text-xs tracking-[0.3em]">Nadie inscripto aún</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Phone Verification Modal */}
      <WhatsAppVerificationModal
        isOpen={showPhonePrompt}
        onClose={() => setShowPhonePrompt(false)}
        onSuccess={handleSavePhone}
        config={config}
      />

      {/* Confirmation Modal */}
      {showConfirm && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/10 backdrop-blur-sm" onClick={() => setShowConfirm(false)} />
          <div className="relative w-full max-w-sm bg-slate-800/80 backdrop-blur-md border border-white/10 rounded-[40px] p-8 shadow-2xl animate-in zoom-in-95 duration-300">
            <div className="flex flex-col items-center text-center space-y-6">
              <div className="w-12 h-12 rounded-full theme-primary-bg/10 flex items-center justify-center border-2 border-dashed theme-primary-border/20">
                <Trophy size={24} className="theme-primary-text animate-bounce-slow" />
              </div>

              <div className="space-y-2">
                <h3 className="text-xl font-black uppercase italic theme-primary-text leading-tight">Elige a tu pareja</h3>
                <p className="text-[10px] font-bold opacity-60 uppercase leading-relaxed px-4">
                  Se enviará una notificación a tu amigo, y una solicitud de inscripción al organizador.
                </p>
              </div>

              <div className="w-full text-left space-y-3 relative z-50">
                <label className="text-[10px] uppercase font-black tracking-widest opacity-40 ml-2">Selecciona a tu pareja</label>
                <div className="relative">
                  <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-30" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Buscar jugador..."
                    value={partnerSearch}
                    onChange={e => setPartnerSearch(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-xl p-2.5 pl-8 font-bold text-xs outline-none focus:border-white/30 focus:bg-white/10 transition-colors"
                  />
                </div>
                
                <div className="space-y-2 max-h-[160px] overflow-y-auto custom-scrollbar">
                    {filteredPartners.slice(0, partnerSearch ? undefined : 4).map(p => (
                      <button
                        key={p.id}
                        onClick={() => setSelectedPartnerId(p.id)}
                        className={`w-full flex items-center gap-3 p-2 rounded-xl border transition-all ${
                          selectedPartnerId === p.id 
                            ? 'border-theme-primary bg-theme-primary/10' 
                            : 'border-white/5 bg-black/20 hover:bg-white/5'
                        }`}
                      >
                        <div className="w-8 h-8 rounded-full overflow-hidden bg-white/10 shrink-0">
                          {p.photoUrl ? (
                            <img src={p.photoUrl} alt={p.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-[10px] font-bold opacity-50">
                              {p.name.charAt(0)}
                            </div>
                          )}
                        </div>
                        <div className="text-left flex-1 min-w-0">
                          <p className="font-bold text-xs truncate text-white">{p.name}</p>
                          <p className="text-[9px] text-white/50 truncate uppercase">{p.category || 'Sin categoría'}</p>
                        </div>
                        {selectedPartnerId === p.id && (
                          <CheckCircle2 size={14} className="theme-primary-text shrink-0" />
                        )}
                      </button>
                    ))}
                    
                    {filteredPartners.length === 0 && (
                      <p className="text-[9px] opacity-40 font-bold uppercase text-center py-2">
                        {partnerSearch ? 'No se encontraron jugadores con ese nombre' : 'No hay jugadores disponibles'}
                      </p>
                    )}
                  </div>

                {!partnerSearch && filteredPartners.length > 4 && (
                  <button
                    onClick={() => searchInputRef.current?.focus()}
                    className="w-full py-2 text-[10px] font-bold uppercase tracking-widest text-white/50 hover:text-white transition-colors"
                  >
                    Buscar a mi pareja
                  </button>
                )}
                {selectedPartnerId && validationErrors.length > 0 && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 space-y-1">
                    {validationErrors.map((err, i) => (
                      <p key={i} className="text-[9px] text-red-500 font-black uppercase tracking-tight flex items-center gap-1.5">
                        <Shield size={10} className="shrink-0" /> {err}
                      </p>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 w-full pt-4">
                <button
                  onClick={() => {
                    setShowConfirm(false);
                    setSelectedPartnerId('');
                    setPartnerSearch('');
                  }}
                  className="px-6 py-4 rounded-2xl bg-white/5 font-black uppercase text-[10px] tracking-widest hover:bg-white/10 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  disabled={!selectedPartnerId || !isDataValid}
                  onClick={() => {
                    if (selectedPartnerId && isDataValid) {
                      onJoin?.(tournament.id, selectedPartnerId);
                      setShowConfirm(false);
                      setSelectedPartnerId('');
                      setPartnerSearch('');
                    }
                  }}
                  className="px-6 py-4 rounded-2xl theme-primary-bg text-black font-black uppercase text-[10px] tracking-widest shadow-xl hover:scale-105 transition-all disabled:opacity-30 disabled:grayscale disabled:cursor-not-allowed"
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
